import { useMutation, useQuery } from '@tanstack/react-query';
import { FormApi } from 'final-form';
import * as R from 'ramda';
import React, { useCallback, useContext, useMemo } from 'react';
import toast from 'react-hot-toast';

import { updateVenueInventory } from 'apis/inventory-management-service/api';
import { getPickingAreas, searchItemsInPickingAreas, updateItemConfiguration } from 'apis/picker-api/api';
import { assignItemsToPickingArea, unassignItemsFromArea, updateItemsInPickingArea } from 'apis/picker-api/api';
import { ItemConfiguration } from 'apis/picker-api/types';
import { getBrandById, searchActiveBrands } from 'apis/product-information-catalog/api';
import {
  getProductInformationSettings,
  updateItem,
  createItemBinding,
  deleteItemBinding,
  getMenuConfigs,
} from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item, ItemCommission, Menu, Option } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { updateItemPricing } from 'apis/restaurant-api/merchant-admin/merchant/api';
import ErrorToastContent from 'common/components/ErrorToastContent';
import { useRetailContext } from 'common/hooks';
import { localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { MenuMetaContext } from 'modules/menu/MenuMetaContext';
import { MenuFeaturesContext } from 'modules/menu/components/MenuFeaturesContext';
import { useVatCategories } from 'modules/menu/components/VatCategories';
import { getModifiedFieldsForTracking, useSaveItemChangesTracking } from 'modules/menu/hooks/telemetryHooks';
import { useProcessMenuImage } from 'modules/menu/hooks/useProcessMenuImage';
import { UNASSIGNED_AREA_ID } from 'modules/menu/items/constants';
import {
  transformCommissionsFormValuesToApi,
  transformItemApiToFormValues,
  transformItemFormValuesToUpdateApi,
  transformStorageLocationFormValueToApi,
} from 'modules/menu/items/transformers';
import { ItemQuantity } from 'modules/menu/items/types';
import useMenuData from 'modules/menu/useMenuData';
import { getParentCategory } from 'modules/menu/utils';

import ItemDetailsEditForm from './ItemDetailsEditForm';
import ItemDetailsView from './ItemDetailsView';
import { MenuLocaleContext } from '../../../../MenuLocaleContext';
import * as behavioral from '../../../../behavioral';
import { getItemCategory } from '../../../utils';
import { FormValues, ValidationContext, validationSchema, ValidFormValues } from '../../ItemDetailsForm';
import { cleanUpInvalidPrerequisiteOptionChoices } from '../../utils';

type Props = {
  className?: string;
  item: Item;
  menu: Menu;
  itemQuantity: ItemQuantity | undefined;
  editing: boolean;
  onSaveSuccess: () => void;
  onItemBalanceUpdated: () => void;
  onCancel: () => void;
  options: Option[];
  itemConfig: ItemConfiguration | undefined;
  itemCommissions: ItemCommission[] | undefined;
  onItemConfigUpdated: (updatedConfigs: Record<string, ItemConfiguration>) => void;
  onItemCommissionsUpdated: () => void;
};

const changeItemCategory = async (newCategoryId: string | null, itemId: string, menu: Menu) => {
  if (newCategoryId) {
    return createItemBinding(menu.id, newCategoryId, { item_id: itemId });
  } else {
    const itemCategory = getItemCategory(menu.categories, itemId);
    if (!itemCategory) {
      throw new Error('Item category not found');
    }
    const itemBinding = itemCategory.item_bindings.find(itemBinding => itemBinding.item_id === itemId);
    if (!itemBinding) {
      throw new Error('Item binding not found');
    }
    return deleteItemBinding(menu.id, itemCategory.id, itemBinding.id);
  }
};

const commissionsProperties: (keyof FormValues)[] = [
  'homedeliveryRelativeCommission',
  'takeawayRelativeCommission',
  'eatinRelativeCommission',
];

const nonSpecificItemProperties: (keyof FormValues)[] = ['categoryId', 'inventoryBalance', ...commissionsProperties];

const hasCategoryChanged = (previousValues: FormValues, currentValues: FormValues) =>
  previousValues.categoryId !== currentValues.categoryId;

const hasItemChanged = (previousValues: FormValues, currentValues: FormValues) =>
  !R.equals(R.omit(nonSpecificItemProperties, previousValues), R.omit(nonSpecificItemProperties, currentValues));

const hasBalanceChanged = (previousValues: FormValues, currentValues: FormValues) =>
  previousValues.inventoryBalance !== currentValues.inventoryBalance;

const haveCommissionsChanged = (previousValues: FormValues, currentValues: FormValues) =>
  !R.equals(R.pick(commissionsProperties, previousValues), R.pick(commissionsProperties, currentValues));

const hasItemStorageLocationChanged = (previousValues: FormValues, currentValues: FormValues) =>
  previousValues.location !== currentValues.location;

const ItemDetails = ({
  className,
  item,
  menu,
  itemQuantity,
  editing,
  onSaveSuccess,
  onItemBalanceUpdated,
  onCancel,
  options,
  itemConfig,
  itemCommissions,
  onItemConfigUpdated,
  onItemCommissionsUpdated,
}: Props) => {
  const { alcohol_warning_permille_limit: alcoholWarningPermilleLimit } = useCountryConfigContext();
  const itemCategory = getItemCategory(menu.categories, item.id);
  const { categories, updateMenuData } = useMenuData();
  const { hasCatalogSource, hasReturnPoliciesEnabled, externalInfoHyperlinkAllowed } = useContext(MenuFeaturesContext);
  const { menuId } = useContext(MenuMetaContext);
  const parentCategory = itemCategory ? getParentCategory(itemCategory.id, categories) : null;

  const { primaryLanguage } = useContext(MenuLocaleContext);
  const isRetail = useRetailContext();
  const { venueId, merchantId } = useContext(MenuMetaContext);
  const processMenuImage = useProcessMenuImage();

  const { data: productInfoSettings } = useQuery(
    [`product-information-settings/${menu.id}`],
    () => getProductInformationSettings(menu.id),
    { refetchOnMount: false, refetchOnWindowFocus: false },
  );

  const { data: brand } = useQuery(
    ['product-brand', item.brand_id],
    () => (!item.brand_id ? Promise.resolve(null) : getBrandById(item.brand_id)),
    { enabled: !!item.brand_id },
  );

  const { data: pickingAreas } = useQuery(['get-picking-areas', venueId], () => getPickingAreas(venueId), {
    enabled: isRetail,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const { data: pickingAreaSearchResult, refetch: revalidatePickingAreaSearch } = useQuery(
    ['search-picking-area', venueId, item.id],
    () => searchItemsInPickingAreas(venueId, { items: [item.id] }),
    { enabled: isRetail },
  );

  const { data: menuConfigs } = useQuery(['menu-configs', menuId, hasCatalogSource], () => getMenuConfigs(menuId), {
    staleTime: Infinity,
    enabled: hasCatalogSource || hasReturnPoliciesEnabled,
  });

  const pickingAreaItemAttrs = useMemo(() => {
    if (!pickingAreaSearchResult?.pickingAreas.length) {
      return undefined;
    }
    const [area] = pickingAreaSearchResult.pickingAreas;
    const [item] = area.items;
    return {
      pickingAreaId: area.id,
      pickingAreaName: area.name,
      pickingItemSequence: area.id !== UNASSIGNED_AREA_ID ? item.order : undefined,
    };
  }, [pickingAreaSearchResult?.pickingAreas]);

  const commissions = useMemo(
    () => (itemCommissions ? itemCommissions.filter(commission => commission.item_id === item.id) : []),
    [item.id, itemCommissions],
  );

  const { data: vatCategories } = useVatCategories();

  const initialFormValues = useMemo(
    () =>
      transformItemApiToFormValues(item, {
        alcoholWarningPermilleLimit,
        productInformationSettings: productInfoSettings,
        language: primaryLanguage,
        categoryId: itemCategory?.id || null,
        itemBalance: itemQuantity?.balance,
        vatCategories: vatCategories?.list,
        commissions,
        itemConfig,
        pickingAreaItemAttrs,
      }),
    [
      item,
      alcoholWarningPermilleLimit,
      productInfoSettings,
      primaryLanguage,
      itemCategory?.id,
      itemQuantity?.balance,
      vatCategories?.list,
      commissions,
      itemConfig,
      pickingAreaItemAttrs,
    ],
  );

  const handleSubmitSuccess = useCallback(() => {
    revalidatePickingAreaSearch();
    onSaveSuccess();
  }, [onSaveSuccess, revalidatePickingAreaSearch]);

  const handleCancelItemDetailsEdit = useCallback(() => {
    behavioral.reportItemEditCancel();
    onCancel();
  }, [onCancel]);

  const loadBrandOptions = useCallback(
    (search: string) =>
      searchActiveBrands(search).then(brands =>
        brands.map(brand => ({
          label: localize(brand.name.translations, brand.name.primaryLanguage),
          value: brand.id,
        })),
      ),
    [],
  );

  const trackSaveChanges = useSaveItemChangesTracking();

  const { mutateAsync: mutateItemCategory } = useMutation(
    async (categoryId: string) => changeItemCategory(categoryId, item.id, menu),
    {
      onSuccess: updatedMenu => {
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
        behavioral.reportItemCategoryChange('itemDetailsForm');
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: mutateItem } = useMutation(
    async ({
      formValues,
      nutritionFactsFormat,
    }: {
      formValues: FormValues;
      nutritionFactsFormat: 'text' | 'table';
    }) => {
      const context: ValidationContext = { currency: item.baseprice.currency };
      const validValues = validationSchema.validateSync(formValues, { context });

      const imageUrls = await Promise.all(
        validValues.image.map(async image => {
          return await processMenuImage(image);
        }),
      );
      const definedImageUrls = imageUrls.filter((url: string | null): url is string => url !== null);

      const haveOptionBindingsChanged = !R.equals(initialFormValues.optionBindings, validValues.optionBindings);

      const optionBindings = haveOptionBindingsChanged
        ? cleanUpInvalidPrerequisiteOptionChoices(validValues.optionBindings, initialFormValues.optionBindings, options)
        : validValues.optionBindings;

      const validValuesWithCleanedUpOptionBindings: ValidFormValues = {
        ...validValues,
        optionBindings,
      };

      const payload = transformItemFormValuesToUpdateApi(
        item,
        validValuesWithCleanedUpOptionBindings,
        definedImageUrls,
        {
          language: primaryLanguage,
          nutritionFactsFormat,
          externalInfoHyperlinkAllowed,
        },
      );
      return updateItem(item.menu_id, item.id, payload);
    },
    {
      onMutate: () => {
        behavioral.reportItemEdit();
      },
      onSuccess: updatedItem => {
        updateMenuData({ actionType: 'updateItem', payload: updatedItem });
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: mutateVenueInventory } = useMutation(
    (inventoryBalance: number) => updateVenueInventory(venueId, item.id, inventoryBalance),
    {
      onSuccess: onItemBalanceUpdated,
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: mutateCommissions } = useMutation(
    async (formValues: FormValues) => {
      const context: ValidationContext = { currency: item.baseprice.currency };
      const validValues = validationSchema.validateSync(formValues, { context });
      await updateItemPricing(merchantId, item.id, transformCommissionsFormValuesToApi(validValues));
    },
    {
      onSuccess: onItemCommissionsUpdated,
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: mutateStorageLocation } = useMutation(
    async (formValues: FormValues) => {
      const context: ValidationContext = { currency: item.baseprice.currency };
      const validValues = validationSchema.validateSync(formValues, { context });
      const itemConfigUpdatePayload = {
        itemId: item.id,
        menuId: menu.id,
        storageLocation: transformStorageLocationFormValueToApi(validValues),
      };

      return updateItemConfiguration(itemConfigUpdatePayload);
    },
    {
      onSuccess: updatedItemConfig => {
        onItemConfigUpdated({ [updatedItemConfig.itemId]: updatedItemConfig });
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const { mutateAsync: mutatePickingArea } = useMutation(
    async (formValues: FormValues) => {
      const { pickingAreaId: prevAreaId, pickingItemSequence: prevSequence } = initialFormValues;
      const { pickingAreaId: nextAreaId, pickingItemSequence: nextSequence } = formValues;
      if (!nextAreaId || !prevAreaId) {
        return;
      }
      const pickingAreaChanged = prevAreaId !== nextAreaId;
      const sequenceChanged = prevSequence !== nextSequence;
      if (pickingAreaChanged && nextAreaId === UNASSIGNED_AREA_ID) {
        await unassignItemsFromArea(venueId, nextAreaId, [item.id]);
      }
      if (pickingAreaChanged && nextAreaId !== UNASSIGNED_AREA_ID) {
        await assignItemsToPickingArea(venueId, nextAreaId, [item.id]);
      }
      if ((pickingAreaChanged || sequenceChanged) && nextSequence !== undefined) {
        const areaId = pickingAreaChanged ? nextAreaId : prevAreaId;
        await updateItemsInPickingArea(venueId, areaId, [{ id: item.id, order: nextSequence }]);
      }
    },
    {
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const handleItemDetailsFormSubmit = useCallback(
    async (formValues: FormValues, nutritionFactsFormat: 'text' | 'table', form: FormApi<FormValues>) => {
      const { categoryId, inventoryBalance } = formValues;

      const results = await Promise.allSettled([
        hasCategoryChanged(initialFormValues, formValues) && categoryId && mutateItemCategory(categoryId),
        hasItemChanged(initialFormValues, formValues) && mutateItem({ formValues, nutritionFactsFormat }),
        hasBalanceChanged(initialFormValues, formValues) &&
          inventoryBalance !== undefined &&
          mutateVenueInventory(inventoryBalance),
        hasItemStorageLocationChanged(initialFormValues, formValues) && mutateStorageLocation(formValues),
        mutatePickingArea(formValues),
      ]);

      // Commission update operation has a dependency on the previous ones to be completed
      // (Backend will send an event to financial-data-registry relying on up-to-date item information)
      const [commissionUpdateResult] = await Promise.allSettled([
        haveCommissionsChanged(initialFormValues, formValues) && mutateCommissions(formValues),
      ]);

      const rejectedPromises = results.concat(commissionUpdateResult).filter(res => res.status === 'rejected');

      if (rejectedPromises.length === 0) {
        handleSubmitSuccess();
        const modifiedFields = getModifiedFieldsForTracking(form.getState());
        trackSaveChanges(item.id, !!formValues.image.length, formValues.mandatoryWarnings, modifiedFields);
      }
    },
    [
      item.id,
      mutatePickingArea,
      initialFormValues,
      mutateItemCategory,
      mutateItem,
      mutateVenueInventory,
      mutateCommissions,
      mutateStorageLocation,
      handleSubmitSuccess,
      trackSaveChanges,
    ],
  );

  return editing ? (
    <ItemDetailsEditForm
      categories={menu.categories}
      vatCategories={vatCategories?.list}
      className={className}
      currency={item.baseprice.currency}
      initialValues={initialFormValues}
      productInfoSettings={productInfoSettings}
      itemQuantity={itemQuantity}
      pickingAreas={pickingAreas}
      syncedFieldNames={menuConfigs?.source_config?.synced_field_names ?? []}
      brand={brand ?? null}
      loadBrandOptions={loadBrandOptions}
      onSubmit={handleItemDetailsFormSubmit}
      onCancel={handleCancelItemDetailsEdit}
      returnPolicies={menuConfigs?.item_return_policies}
    />
  ) : (
    <ItemDetailsView
      returnPolicies={menuConfigs?.item_return_policies}
      className={className}
      category={itemCategory || null}
      parentCategory={parentCategory || null}
      item={item}
      productInfoSettings={productInfoSettings}
      pickingAreaItemAttrs={pickingAreaItemAttrs}
      itemQuantity={itemQuantity}
      commissions={commissions}
      itemConfig={itemConfig}
      brand={brand ?? null}
    />
  );
};

export default ItemDetails;
